import React from "react";

import Topbar from "../../components/topbar";
import AddInvoiceProviderModal from "../../components/popup/add-invoice-provider";
import type { Column } from '../../components/admin-table';
import { Box } from "@mui/material";
import AddBtn from "../../components/ui/add-btn";
import DynamicAdminTable from "../../components/admin-table";
import Pagination from "../../components/pagination";
import DeletePopup from "../../components/popup/delete";
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const PAGE_SIZE = 10;

type InvoiceProvider = {
    id: string;
    providerName: string;
    country: string;
    importFormat: string;
};

const InvoiceProviderPage: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [data, setData] = React.useState<InvoiceProvider[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedProvider, setSelectedProvider] = React.useState<any>(null);
    const [page, setPage] = React.useState(1);

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.invoiceProviders.fetch, [{ param: 'includeAllActive', value: true }]);
        setFetch({ url, method });
    }, [page, create, update, remove, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data.map((prov: any) => ({
                id: prov.id,
                providerName: prov.name,
                country: prov.country,
                importFormat: typeof prov.importFormat === 'string' ? prov.importFormat : JSON.stringify(prov.importFormat),
            })));
        }
        if (error) {
            showError("Failed to fetch invoice providers");
        }
    }, [fetch, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess("Provider created successfully");
        }
        if (error) {
            showError("Failed to create provider");
        }
    }, [create, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess("Provider updated successfully");
        }
        if (error) {
            showError("Failed to update provider");
        }
    }, [update, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            showSuccess("Provider deleted successfully");
        }
        if (error) {
            showError("Failed to delete provider");
        }
    }, [remove, showSuccess, showError]);

    const handleCreate = (providerData: any) => {
        const { url, method } = buildApiRequestConfig(routes.invoiceProviders.create);
        setCreate({ url, method, body: providerData });
        setOpenModal('');
    }

    const handleUpdate = (providerData: any) => {
        const { url, method } = buildApiRequestConfig(routes.invoiceProviders.update, [{ param: 'id', value: selectedProvider.id }]);
        setUpdate({ url, method, body: providerData });
        setOpenModal('');
    }

    const handleDelete = (id: string) => {
        const { url, method } = buildApiRequestConfig(routes.invoiceProviders.delete, [{ param: 'id', value: id }]);
        setRemove({ url, method });
    }

    const columns: Column[] = [
        { id: 'providerName', label: ' Provider', size: 150, onSort: (direction) => console.log(`Sort by provider ${direction}`) },
        { id: 'country', label: 'Country', size: 150, onSort: (direction) => console.log(`Sort by country ${direction}`) },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 50, 
            type: 'actions', 
            onDelete: (row) => {
                setSelectedProvider(row);
                setOpenModal('delete');
            },
            onEdit: (row) => {
                setSelectedProvider(row);
                setOpenModal('edit');
            }
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddInvoiceProviderModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                onConfirm={openModal === 'add' ? handleCreate : handleUpdate}
                isEdit={{ id: selectedProvider?.id, state: openModal === 'edit' }}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Provider?"
                description="This action will permanently remove the selected provider."
                onCancel={() => setOpenModal('')}
                image={{ src: '', width: 0, height: 0 }}
                onConfirm={() => {
                    if(!selectedProvider) return;
                    handleDelete(selectedProvider.id);
                    setOpenModal('');
                }}
            />
            <Topbar title="Invoice Provider" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="flex-end">
                    <AddBtn onClick={() => setOpenModal('add')} label="Add Provider" />
                </Box>
                <DynamicAdminTable 
                    columns={columns} 
                    data={data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)} 
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={Math.ceil(data.length / PAGE_SIZE)}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={data.length}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default InvoiceProviderPage;
